// src/features/task/services/taskQueries.ts
// 讀取查詢：getAllTasks, getTaskById, getClosedTasksPaginated,
// getTasksByObjectiveId, getTasksByIdsFromCache
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Task } from '../../../types/task';
import { logger } from '../../../utils/logger';
import {
  TASKS_COLLECTION,
  convertFirestoreTaskToTask,
  hasTaskChanged,
} from './taskConverters';

// 降級方案：只獲取 OPEN 任務（避免拉回歷史 CLOSED 任務）
const getAllTasks = async (userId: string): Promise<Task[]> => {
  try {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'OPEN'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    const tasks: Task[] = [];
    querySnapshot.forEach((d) => {
      tasks.push(convertFirestoreTaskToTask(d));
    });

    return tasks;
  } catch (error) {
    logger.error('獲取任務失敗:', error);
    throw new Error('獲取任務失敗');
  }
};

// 分頁獲取 CLOSED 任務（供 CLOSED tab 按需載入）
const getClosedTasksPaginated = async (
  userId: string,
  options?: {
    limit?: number;
    startAfterDoc?: DocumentSnapshot;
  }
): Promise<{
  tasks: Task[];
  hasMore: boolean;
  lastDoc: DocumentSnapshot | null;
}> => {
  try {
    const pageSize = options?.limit ?? 30;

    let q = query(
      collection(db, TASKS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'CLOSED'),
      orderBy('closedAt', 'desc'),
      limit(pageSize + 1) // 多取 1 筆來判斷是否有下一頁
    );

    if (options?.startAfterDoc) {
      q = query(q, startAfter(options.startAfterDoc));
    }

    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    const hasMore = docs.length > pageSize;
    const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

    return {
      tasks: pageDocs.map(d => convertFirestoreTaskToTask(d)),
      hasMore,
      lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    };
  } catch (error) {
    logger.error('分頁獲取 CLOSED 任務失敗:', error);
    throw new Error('獲取已完成任務失敗');
  }
};

// 根據 objectiveId 取得關聯任務（所有狀態）
const getTasksByObjectiveId = async (objectiveId: string, userId: string): Promise<Task[]> => {
  try {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('userId', '==', userId),
      where('objectiveId', '==', objectiveId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => convertFirestoreTaskToTask(d));
  } catch (error) {
    logger.error('getTasksByObjectiveId 失敗:', error);
    throw new Error('獲取關聯任務失敗');
  }
};

// 取得單一任務詳情
const getTaskById = async (taskId: string, userId: string): Promise<Task | null> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (!taskDoc.exists()) {
      return null;
    }

    const task = convertFirestoreTaskToTask(taskDoc);

    // 驗證任務屬於該用戶
    if (task.userId !== userId) {
      logger.warn('嘗試訪問不屬於用戶的任務:', taskId);
      return null;
    }

    return task;
  } catch (error) {
    logger.error('獲取任務詳情失敗:', error);
    throw new Error('獲取任務詳情失敗');
  }
};

// Local-first: 批次獲取任務（優先從 IndexedDB）
const getTasksByIdsFromCache = async (
  taskIds: string[],
  userId: string,
  options?: {
    onCacheHit?: (cachedTasks: Task[]) => void;
    syncInBackground?: boolean;
  }
): Promise<Task[]> => {
  if (!taskIds.length) return [];

  try {
    // 動態載入 taskRepository（避免循環依賴）
    const { taskRepository } = await import('../../../services/indexedDB/repositories/taskRepository');

    // 1. 優先從 IndexedDB 讀取
    const cachedTasks: Task[] = [];
    const missedTaskIds: string[] = [];

    for (const taskId of taskIds) {
      const cached = await taskRepository.getById(taskId);
      if (cached && cached.userId === userId) {
        cachedTasks.push(cached);
      } else {
        missedTaskIds.push(taskId);
      }
    }

    // 若有快取命中，立即回調
    if (cachedTasks.length > 0 && options?.onCacheHit) {
      options.onCacheHit(cachedTasks);
    }

    // 2. 從 Firestore 獲取缺失的任務
    const freshTasks: Task[] = [];

    if (missedTaskIds.length > 0) {
      const fetchPromises = missedTaskIds.map(id => getTaskById(id, userId));
      const results = await Promise.allSettled(fetchPromises);

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          freshTasks.push(result.value);
          taskRepository.createLocal(result.value).catch(err => {
            logger.error('寫入本地快取失敗:', err);
          });
        }
      }
    }

    // 3. 若需要背景同步，驗證快取任務是否為最新
    if (options?.syncInBackground !== false && cachedTasks.length > 0) {
      Promise.all(
        cachedTasks.map(async (cachedTask) => {
          const fresh = await getTaskById(cachedTask.id, userId);
          if (fresh && hasTaskChanged(cachedTask, fresh)) {
            await taskRepository.createLocal(fresh);
          }
        })
      ).catch(err => {
        logger.error('Background task sync failed', err);
      });
    }

    // 合併快取與新獲取的任務，保持原始順序
    const taskMap = new Map<string, Task>();
    [...cachedTasks, ...freshTasks].forEach(task => {
      taskMap.set(task.id, task);
    });

    return taskIds
      .map(id => taskMap.get(id))
      .filter((task): task is Task => task !== undefined);

  } catch (error) {
    logger.error('批次獲取任務失敗', error);
    // 降級到直接從 Firestore 獲取
    const results = await Promise.allSettled(taskIds.map(id => getTaskById(id, userId)));
    return results
      .filter((r): r is PromiseFulfilledResult<Task | null> => r.status === 'fulfilled')
      .map(r => r.value)
      .filter((task): task is Task => task !== null);
  }
};

export const taskQueryMethods = {
  getAllTasks,
  getClosedTasksPaginated,
  getTasksByObjectiveId,
  getTaskById,
  getTasksByIdsFromCache,
};
